import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { swal, typeChecker } from '../../../utils/methods';
import { _post } from '../../../utils/network';
import { toShamsiDate } from '../blogUtils';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from '../../../components/Form/FormField/FormField.component';
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';

const BlogCategoriesCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const formRef = useRef(null);
	const [saveButtonIsLoading, setSaveButtonIsLoading] = useState(false);
	const [deleteButtonIsLoading, setDeleteButtonIsLoading] = useState(false);
	const [blogCategory, setBlogCategory] = useState({});

	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('blog_categories'), link: '/blog-categories-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const saveBlogCategory = event => {
		event.preventDefault();
		if(saveButtonIsLoading)
			return;
		const title = event.target.blogCategoryTitle.value.trim();
		if(!typeChecker(title, 'title'))
			return toast.warn(t('title') + ' ' + t('must_have_at_least_3_characters'));
		const body = {
			id,
			title,
			is_active: event.target.blogCategoryIsActive.checked,
			more: blogCategory?.more || {}
		};
		confirmSwal(async () => {
			setSaveButtonIsLoading(true);
			const response = await _post(`/blog_categories/admin/${componentType === 'create' ? 'create' : 'update'}BlogCategory`, body);
			setSaveButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					if(componentType === 'create')
						event.target.reset();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const deleteBlogCategory = () => {
		if(deleteButtonIsLoading)
			return;
		confirmSwal(async () => {
			setDeleteButtonIsLoading(true);
			const response = await _post('/blog_categories/admin/deleteBlogCategory', { id });
			setDeleteButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					navigate('/blog-categories-list');
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const getBlogCategory = async () => {
		const response = await _post('/blog_categories/admin/getOneBlogCategory', { id });
		switch(response.code){
			case 200:
				setBlogCategory(response.data.blog_category);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	useEffect(() => {
		if(componentType === 'edit')
			getBlogCategory();
	}, []);
	useEffect(() => {
		if(componentType === 'create')
			formRef.current?.reset();
	}, [location]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t(componentType)} {t('blog_category')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={saveBlogCategory} ref={formRef}>
						<div className="row gx-2">
							<FormField fieldKey="blogCategoryTitle" title={t('title')} value={blogCategory?.title} required />
							<FormField type="toggle" fieldKey="blogCategoryIsActive" title={t('active')} value={blogCategory?.is_active} required />
							{componentType === 'edit' &&
								<>
									<FormField fieldKey="blogCategoryCreatedAt" title={t('creation_date')} value={toShamsiDate(blogCategory?.created_at)} disabled ltr />
									<FormField fieldKey="blogCategoryUpdatedAt" title={t('update_date')} value={toShamsiDate(blogCategory?.updated_at)} disabled ltr />
								</>
							}
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name={componentType} isLoading={saveButtonIsLoading}>
								<i className="fa fa-save"></i> {t(componentType === 'create' ? 'create' : 'update')}
							</LoadingButton>
							{componentType === 'edit' &&
								<LoadingButton className="btn btn-danger d-inline-block mt-3" onClick={deleteBlogCategory} type="button" name="delete" isLoading={deleteButtonIsLoading}>
									<i className="fa fa-trash"></i> {t('delete')}
								</LoadingButton>
							}
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default BlogCategoriesCreate;
